import React from 'react';
import { useTokenOps } from '../components/TokenContext';
import CommercialIntelligencePanel from './CommercialIntelligencePanel';
import CommercialGovernancePanel from './CommercialGovernancePanel';
import { CommercialPage, CommercialPageHeader } from './CommercialUi';
import { CommercialSectionNavigation, CommercialTaskGuide } from './CommercialNavigation';

const copy = {
  metrics: ['Revenue metrics', 'MRR movement, retention and recurring-revenue health.'],
  sales: ['Sales', 'Prioritise opportunities, record next actions and keep the pipeline moving.'],
  profitability: ['Profitability', 'Connect revenue to service and acquisition costs to understand contribution.'],
  automation: ['Workflow modes', 'Choose whether each workflow starts automatically or from its owning business page.'],
  templates: ['Email templates', 'Edit reusable customer communications and preview them with real customer data.'],
  history: ['Workflow history', 'Review the outcome of onboarding, invitations and renewal outreach.'],
  audit: ['Audit trail', 'Review sensitive commercial changes and automation decisions.'],
};

const guidance = {
  metrics: {
    when: 'A reporting period closes or an MRR movement needs explanation.',
    changes: 'Read-only. Filters change this view; they never change customer revenue.',
    start: 'Review the scorecard, then inspect the affected-customer movement that needs explanation.',
  },
  sales: {
    when: 'An opportunity needs contact details, a dated next step, a stage decision, or customer handoff.',
    changes: 'Saving updates the opportunity; a won outcome can start the reviewed onboarding workflow.',
    start: 'Work from the highest-priority row and complete its primary action.',
  },
  profitability: {
    when: 'Customer delivery costs or acquisition spend are ready to compare with revenue.',
    changes: 'Read-only. Results change only when their source cost or revenue records change.',
    start: 'Check cost-data coverage, then add the missing cost record named in the prompt.',
  },
  automation: {
    when: 'You need to change whether a workflow waits for review or starts automatically.',
    changes: 'Saving changes future workflow behaviour only; it does not start a workflow.',
    start: 'Verify one rule\'s triggers and prerequisites, choose its mode, then save that mode.',
  },
  templates: {
    when: 'Customer-facing email copy or recipients need a new approved version.',
    changes: 'Saving creates and activates a new template version for future workflow runs.',
    start: 'Open or create one template, preview with a real customer, then save the new version.',
  },
  history: {
    when: 'A workflow is failed, blocked, unfinished, or needs its outcome verified.',
    changes: 'Mostly read-only. Credential recovery rotates the password and resends the welcome email.',
    start: 'Keep Needs attention selected, open the source record, then use recovery only when offered.',
  },
  audit: {
    when: 'You need to verify who changed a sensitive commercial record and what changed.',
    changes: 'Read-only. Reviewing an audit entry never modifies the underlying record.',
    start: 'Filter by action or entity, then open Review change for the before-and-after details.',
  },
};

const performanceSections = new Set(['metrics', 'profitability']);
const operationsSections = new Set(['automation', 'templates', 'history', 'audit']);

export default function CommercialSectionPage({ section }) {
  const token = useTokenOps().account.accessToken;
  const [title, description] = copy[section] || ['Commercial', 'Commercial workspace'];
  const governance = operationsSections.has(section);
  const navigationArea = performanceSections.has(section) ? 'performance' : operationsSections.has(section) ? 'operations' : null;
  return <CommercialPage>
    <CommercialPageHeader title={title} description={description} />
    {navigationArea && <CommercialSectionNavigation area={navigationArea} current={section} />}
    <CommercialTaskGuide title={title} {...guidance[section]} />
    {governance ? <CommercialGovernancePanel token={token} section={section} /> : <CommercialIntelligencePanel token={token} section={section} />}
  </CommercialPage>;
}
